<script lang="ts">
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Menu from "@components/layout/Menu.svelte";
	import { onMount } from "svelte";
	import calendarIcon from "@core/assets/icons/calendar.svg?raw";
	import logo from "@core/assets/logo.svg";
	import type { MenuItem } from "@core/schema/MenuItem.js";
	import { getHomepagePath } from "@core/schema/paths/getHomepagePath.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { lockScroll } from "../../state/lockScroll.svelte.js";
	import Icon from "../common/Icon.svelte";
	import Link from "../common/Link.svelte";

	const { menu, currentPath }: { menu: MenuItem[]; currentPath: string } = $props();
	const { tenant } = getGlobalTemplateProps();

	let mobileMenuOpen = $state(false);
	let isVisible = $state(true);
	let lastScrollY = $state(0);
	let isAtTop = $state(true);

	function toggleMenu(): void {
		mobileMenuOpen = !mobileMenuOpen;
	}

	onMount((): (() => void) => {
		function handleScroll(): void {
			if (lockScroll.lock) {
				return;
			}

			const currentScrollY = window.scrollY;

			isAtTop = currentScrollY <= 80;

			if (currentScrollY < lastScrollY || currentScrollY < 60) {
				isVisible = true;
			} else if (currentScrollY > lastScrollY && currentScrollY > 60) {
				isVisible = false;
			}

			lastScrollY = currentScrollY;
		}

		window.addEventListener("scroll", handleScroll, { passive: true });

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	});
</script>

<header
	class="fixed top-0 left-0 right-0 z-50 grid-container flex items-center justify-center px-4 md:px-8 py-4 md:py-4 transition-all duration-300 md:h-auto"
	class:-translate-y-full={!isVisible}
	style:background-color={isAtTop ? "transparent" : "var(--color-ivory-100, #F5F1EB)"}
>
	<div class="full-width flex flex-row justify-between items-center gap-4 md:grid md:grid-cols-3">
		<button
			type="button"
			class="group flex flex-col items-start justify-between w-7 h-7 flex-shrink-0 md:w-8 md:h-8"
			onclick={toggleMenu}
			aria-label="Menu"
		>
			<div class="flex flex-col gap-[5px] w-7 h-[12px] md:gap-[6px] md:w-8 md:h-[14px]">
				<span
					class="block h-[3px] w-7 bg-[#7F8F77] md:h-1 md:w-8 md:bg-[#7F8F77] md:transition-all md:duration-300 md:group-hover:w-5"
				></span>
				<span
					class="block h-[3px] w-[17.5px] bg-[#7F8F77] md:h-1 md:w-5 md:bg-[#7F8F77] md:transition-all md:duration-300 md:group-hover:w-8"
				></span>
			</div>
			<span
				class="block text-[6.5px] uppercase text-[#7F8F77] mt-[5px] font-extrabold tracking-wider md:text-[9.7px] md:mt-0 md:text-[#7F8F77]"
				>menu</span
			>
		</button>

		<Link
			href={getHomepagePath(tenant)}
			class="absolute left-1/2 -translate-x-1/2 flex items-center justify-center md:relative md:left-0 md:translate-x-0 md:flex-1"
		>
			<img src={logo} alt="Napohodu" class="h-7 w-auto md:h-full md:w-auto" />
		</Link>

		<div class="flex md:hidden justify-end items-center flex-shrink-0">
			<Link
				href="https://head-spa-na-pohodu.reservio.com/"
				target="_blank"
				class="flex flex-row justify-center items-center w-12 h-9 px-4 py-2 gap-2 bg-copper-300 rounded-xl shadow-[0px_4.5px_1.5px_-3px_rgba(0,0,0,0.1)]"
			>
				<Icon icon={calendarIcon} class="w-5 h-5 text-ivory-100" />
			</Link>
		</div>

		<div class="hidden md:flex justify-end flex-shrink-0">
			<Link
				href="https://head-spa-na-pohodu.reservio.com/"
				target="_blank"
				variant={ButtonVariant.PrimarySmall}
				iconStart={calendarIcon}>rezervovat termín</Link
			>
		</div>
	</div>

	<Menu bind:mobileOpen={mobileMenuOpen} items={menu} {currentPath} />
</header>
